<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import BopModelViewer, { type Viewport } from '@/components/bop/BopModelViewer.vue'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import { GlbStatus, PermissionOrganization, StepArticleVisibility } from '@/generated/graphql'
import useAuthorization from '@/composables/useAuthorization'
import {
  useStepMeshHints,
  ancestorsFromPath,
  flattenNodes,
  type StepNodeForViewer,
} from '@/composables/useStepMeshHints'
import ViewerModelPicker, { type ViewerModelOption } from '@/components/bop/ViewerModelPicker.vue'
import { useModuleArticleColorMap } from '@/composables/useStepArticleColors'
import { contentStableComputed } from '@/utils/contentStableComputed'
import type { SelectableHint } from '@/composables/useMeshHighlighting'
import type { StoredArticleTransform } from '@/composables/useArticleTransforms'
import type { PartAllocationRequest } from '@/composables/useViewerSelection'
import type { EbomTreePart } from '@/utils/ebomTree'
import {
  allocationChangeFor,
  findInstanceAllocation,
  instanceCountOf,
  selectingInstances,
  type InstanceAllocationChange,
} from '@/utils/instanceAllocation'

const props = defineProps<{
  bopId: string
  moduleId: string
  stepId?: string
  nodeId?: string
  canSaveViewport?: boolean
  allocatable?: boolean
  showActiveEbomLabel?: boolean
  showControlsHelp?: boolean
  showEbomTree?: boolean
}>()

const emit = defineEmits<{
  allocated: []
}>()

const { t } = useI18n()
const { reactiveOrgAuthz } = useAuthorization()
const canReadEbom = reactiveOrgAuthz(PermissionOrganization.OrgEbomRead)

type BopUploadForViewer = {
  id: string
  glbStatus: string
  glbUpload: { id: string; url: string } | null
}

type BopUploadWithFile = BopUploadForViewer & { upload: { id: string; name: string } }

type ViewerQuery = {
  product: {
    bop: {
      id: string
      uploads: Array<BopUploadWithFile>
      hiddenArticleNumbers: string[]
    }
    module: {
      id: string
      nodes: Array<
        StepNodeForViewer & {
          step: StepNodeForViewer['step'] & {
            activeBopUpload: BopUploadForViewer | null
            preferredBopUploadId: string | null
            articleVisibility: StepArticleVisibility
          }
        }
      >
    }
  }
  article: {
    ebomsForBop?: Array<{
      id: string
      name: string
      bopUpload: { id: string } | null
    }>
  }
}

const { result, refetch, loading } = useQuery<
  ViewerQuery,
  { bopId: string; moduleId: string; includeEboms: boolean }
>(
  gql`
    query BopStepViewerData($bopId: ID!, $moduleId: ID!, $includeEboms: Boolean!) {
      product {
        bop(id: $bopId) {
          id
          uploads {
            id
            glbStatus
            glbUpload {
              id
              url
            }
            upload {
              id
              name
            }
          }
          hiddenArticleNumbers
        }
        module(id: $moduleId) {
          id
          nodes {
            id
            parentId
            effectiveArticleAllocations {
              __typename
              ... on ManualArticleAllocation {
                article {
                  id
                  articleNumber
                  name
                  type
                }
                milliQuantity
              }
              ... on EbomPartAllocation {
                article {
                  id
                  articleNumber
                  name
                  type
                }
                milliQuantity
                instanceIndexes
                ebomPart {
                  id
                  articlePath {
                    articleNumbers
                  }
                }
              }
            }
            effectiveInputs {
              id
              milliQuantity
              output {
                id
                article {
                  articleNumber
                }
              }
              contentArticlePaths {
                articleNumbers
              }
            }
            effectiveOutputs {
              article {
                articleNumber
              }
            }
            step {
              id
              viewport {
                cameraX
                cameraY
                cameraZ
                targetX
                targetY
                targetZ
              }
              articleTransforms {
                articleId
                instanceIndex
                positionX
                positionY
                positionZ
                rotationX
                rotationY
                rotationZ
                rotationW
              }
              activeBopUpload {
                id
                glbStatus
                glbUpload {
                  id
                  url
                }
              }
              preferredBopUploadId
              articleVisibility
            }
          }
        }
      }
      article {
        ebomsForBop(bopId: $bopId) @include(if: $includeEboms) {
          id
          name
          bopUpload {
            id
          }
        }
      }
    }
  `,
  () => ({ bopId: props.bopId, moduleId: props.moduleId, includeEboms: canReadEbom.value }),
)

// Kept out of the viewer query: it lists every EBOM part, and that query is refetched after
// each edit to the step.
const { result: knownArticlesResult } = useQuery<
  {
    article: {
      ebomsForBop: Array<{ id: string; ebomParts: Array<{ id: string; articleNumber: string }> }>
    }
  },
  { bopId: string }
>(
  gql`
    query BopStepViewerKnownArticles($bopId: ID!) {
      article {
        ebomsForBop(bopId: $bopId) {
          id
          ebomParts {
            id
            articleNumber
          }
        }
      }
    }
  `,
  () => ({ bopId: props.bopId }),
  () => ({ enabled: canReadEbom.value }),
)

const knownArticleNames = contentStableComputed<string[]>(() => {
  const eboms = knownArticlesResult.value?.article.ebomsForBop ?? []
  const names = new Set<string>()
  for (const ebom of eboms) {
    for (const p of ebom.ebomParts) names.add(p.articleNumber)
  }
  return [...names]
})

const lastUpload = computed<BopUploadForViewer | null>(
  () => result.value?.product.bop.uploads.at(-1) ?? null,
)

const preferredStepUpload = computed<BopUploadForViewer | null>(() => {
  if (!nodeIdRef.value) return null
  return moduleNodes.value.find((n) => n.id === nodeIdRef.value)?.step.activeBopUpload ?? null
})

const lastDoneUpload = computed<BopUploadForViewer | null>(() => {
  const uploads = result.value?.product.bop.uploads ?? []
  return uploads.findLast((u) => u.glbStatus === GlbStatus.Done) ?? null
})

// A step without its own model most likely continues on the model of the step before it.
const previousStepUpload = computed<BopUploadForViewer | null>(() => {
  const ordered = flattenNodes(moduleNodes.value)
  const idx = ordered.findIndex((n) => n.id === nodeIdRef.value)
  for (let i = idx - 1; i >= 0; i--) {
    const upload = ordered[i].step.activeBopUpload
    if (upload?.glbStatus === GlbStatus.Done) return upload
  }
  return null
})

const displayedUpload = computed<BopUploadForViewer | null>(() => {
  const preferred = preferredStepUpload.value
  if (preferred?.glbStatus === GlbStatus.Done) return preferred
  return previousStepUpload.value ?? lastDoneUpload.value
})

const modelOptions = computed<ViewerModelOption[]>(() => {
  const eboms = result.value?.article.ebomsForBop ?? []
  return (result.value?.product.bop.uploads ?? [])
    .filter((u) => u.glbStatus === GlbStatus.Done && !!u.glbUpload)
    .map((u) => ({
      id: u.id,
      title: eboms.find((e) => e.bopUpload?.id === u.id)?.name ?? u.upload.name,
      fileName: u.upload.name,
    }))
})

const hasSelectedModel = computed(
  () => !!moduleNodes.value.find((n) => n.id === nodeIdRef.value)?.step.preferredBopUploadId,
)

const showModelPicker = computed(
  () => !!props.canSaveViewport && !!props.stepId && modelOptions.value.length > 1,
)

const activeEbomLabel = computed<string | null>(() => {
  if (!props.showActiveEbomLabel) return null
  const eboms = result.value?.article.ebomsForBop ?? []
  if (eboms.length < 2) return null
  const uploadId = displayedUpload.value?.id
  if (!uploadId) return null
  return eboms.find((e) => e.bopUpload?.id === uploadId)?.name ?? null
})

const glbUrl = computed<string | null>(() => displayedUpload.value?.glbUpload?.url ?? null)

const stepIdRef = computed(() => props.stepId)
const moduleNodes = computed(() => result.value?.product.module.nodes ?? [])
// Prefer the explicit placement (the editor passes node-id); fall back to the first node for the step
// so callers that only know the step (the operator/work-instructions view) still resolve a node.
const nodeIdRef = computed(
  () => props.nodeId ?? moduleNodes.value.find((n) => n.step.id === props.stepId)?.id,
)

const savedVisibility = computed<StepArticleVisibility | null>(
  () => moduleNodes.value.find((n) => n.id === nodeIdRef.value)?.step.articleVisibility ?? null,
)
const visibilityMode = ref<StepArticleVisibility>(StepArticleVisibility.AllPrevious)

const pickedVisibility = new Map<string, StepArticleVisibility>()

let syncedStepId: string | undefined
watch(
  [stepIdRef, savedVisibility],
  ([stepId, saved]) => {
    if (stepId === syncedStepId) return
    const next = (stepId ? pickedVisibility.get(stepId) : undefined) ?? saved
    if (next == null) return
    visibilityMode.value = next
    syncedStepId = stepId
  },
  { immediate: true },
)

const {
  visibleMeshHints,
  currentStepHighlightHints,
  moduleTransformHints,
  currentStepArticleTransforms,
  editableTransformHints,
  currentStepEbomPartAllocations,
  savedViewport,
} = useStepMeshHints(nodeIdRef, moduleNodes, visibilityMode)

// Module-wide colours, shared with the article/input legends so model and legend agree.
const colorMap = useModuleArticleColorMap(() => moduleNodes.value)

const selectable = computed(
  () => !!props.allocatable && canReadEbom.value && !!props.stepId && !!glbUrl.value,
)

const currentStepInGroup = computed(
  () => !!moduleNodes.value.find((n) => n.id === nodeIdRef.value)?.parentId,
)

const { result: ebomResult } = useQuery<
  {
    article: {
      ebomsForBop: Array<{
        id: string
        bopUpload: { id: string } | null
        ebomParts: Array<{
          id: string
          parentId: string | null
          articleNumber: string
          name: string | null
          position: string | null
          milliQuantity: number
          article: { id: string } | null
          articlePath: { articleNumbers: string[] }
        }>
      }>
    }
  },
  { bopId: string }
>(
  gql`
    query BopStepViewerEbomParts($bopId: ID!) {
      article {
        ebomsForBop(bopId: $bopId) {
          id
          bopUpload {
            id
          }
          ebomParts {
            id
            parentId
            articleNumber
            name
            position
            milliQuantity
            article {
              id
            }
            articlePath {
              articleNumbers
            }
          }
        }
      }
    }
  `,
  () => ({ bopId: props.bopId }),
  () => ({ enabled: selectable.value }),
)

const displayedUploadId = computed(() => displayedUpload.value?.id)

const displayedEbomParts = computed(() => {
  const eboms = ebomResult.value?.article.ebomsForBop ?? []
  const uploadId = displayedUploadId.value
  const matched = uploadId ? eboms.find((e) => e.bopUpload?.id === uploadId) : null
  return matched ? matched.ebomParts : eboms.flatMap((e) => e.ebomParts)
})

const ebomTreeParts = contentStableComputed<EbomTreePart[]>(() => {
  if (!props.showEbomTree) return []
  return displayedEbomParts.value.map((p) => ({
    id: p.id,
    parentId: p.parentId,
    articleNumber: p.articleNumber,
    name: p.name,
    position: p.position,
  }))
})

const selectableHints = contentStableComputed<SelectableHint[]>(() => {
  const parts = displayedEbomParts.value
  const assemblyIds = new Set(parts.map((p) => p.parentId).filter((id): id is string => !!id))
  return parts
    .filter((p) => !!p.article && !(p.parentId === null && assemblyIds.has(p.id)))
    .map((p) => {
      const numbers = p.articlePath.articleNumbers
      const own = numbers[numbers.length - 1] ?? ''
      return {
        ebomPartId: p.id,
        articleNumber: own,
        glbArticleNames: [own],
        requiredAncestors: ancestorsFromPath(numbers),
        milliQuantity: p.milliQuantity,
        promoted: !!p.article,
      }
    })
})

const allocateMutation = useMutation(gql`
  mutation BopStepViewerAllocateEbomPart($command: AllocateEbomPartToStepCommand!) {
    article {
      allocateEbomPartToStep(command: $command)
    }
  }
`)

const feedbackOpen = ref(false)
const feedback = ref<{ message: string; color: string }>({ message: '', color: '' })
function showFeedback(message: string, color = '') {
  feedback.value = { message, color }
  feedbackOpen.value = true
}

const deleteDialog = ref<ComponentExposed<typeof AsyncDeleteDialog>>()

const overlayAttach = ref<Element | undefined>(undefined)
function syncOverlayAttach() {
  overlayAttach.value = document.fullscreenElement ?? undefined
}
onMounted(() => document.addEventListener('fullscreenchange', syncOverlayAttach))
onBeforeUnmount(() => document.removeEventListener('fullscreenchange', syncOverlayAttach))

// The allocation list keeps its own query, so refetch it by name to reflect changes at once.
const allocationMutateOptions = {
  refetchQueries: ['ArticleAllocationListData', 'StepArticleColors'],
  awaitRefetchQueries: true,
}

function onAllocationChanged() {
  suppressLoading.value = true
  emit('allocated')
  refetch()?.then(() => {
    suppressLoading.value = false
  })
}

function handleUnusablePart() {
  showFeedback(t('component.bopModelViewer.cannotAllocate'))
}

async function changeAllocation(
  hint: SelectableHint,
  change: InstanceAllocationChange,
  feedback?: string,
) {
  const stepId = props.stepId
  if (!stepId) return
  await allocateMutation.mutate(
    { command: { stepId, ebomPartId: hint.ebomPartId, ...change } },
    allocationMutateOptions,
  )
  if (feedback) showFeedback(feedback, 'success')
  onAllocationChanged()
}

function confirmRemoval(hint: SelectableHint) {
  deleteDialog.value?.open(t('entity.ebomPartAllocation.singular'), () =>
    changeAllocation(hint, { milliQuantity: 0, instanceIndexes: null }),
  )
}

function handleAllocatePart({ hint, instanceIndex, allInstances }: PartAllocationRequest) {
  const decision = allocationChangeFor(
    { ebomMilliQuantity: hint.milliQuantity ?? 0, instanceIndex, allInstances },
    findInstanceAllocation(currentStepEbomPartAllocations.value, hint.ebomPartId),
  )
  if (decision.kind === 'remove') {
    confirmRemoval(hint)
    return
  }
  const feedback = decision.allocates ? t('component.bopModelViewer.allocated') : undefined
  changeAllocation(hint, decision.change, feedback)
}

function handleSaveInstances(hint: SelectableHint, instanceIndexes: number[]) {
  changeAllocation(hint, selectingInstances(instanceIndexes, instanceCountOf(hint.milliQuantity)))
}

const saveViewportMutation = useMutation(gql`
  mutation SaveStepViewport($command: SaveStepViewport!) {
    product {
      saveStepViewport(command: $command) {
        cameraX
        cameraY
        cameraZ
        targetX
        targetY
        targetZ
      }
    }
  }
`)

const setVisibilityMutation = useMutation(gql`
  mutation BopStepViewerSetArticleVisibility($command: SetStepArticleVisibility!) {
    product {
      setStepArticleVisibility(command: $command) {
        id
        articleVisibility
      }
    }
  }
`)

const setPreferredModelMutation = useMutation(gql`
  mutation BopStepViewerSetPreferred3dModel($command: SetStepPreferred3dModel!) {
    product {
      setStepPreferred3dModel(command: $command) {
        id
      }
    }
  }
`)

function onSelectModel(bopUploadId: string | null) {
  if (!props.stepId) return
  setPreferredModelMutation.mutate({ command: { stepId: props.stepId, bopUploadId } })?.then(() => {
    suppressLoading.value = true
    refetch()?.then(() => {
      suppressLoading.value = false
    })
  })
}

function onVisibilityModeChange(newMode: StepArticleVisibility) {
  visibilityMode.value = newMode
  if (!props.stepId) return
  pickedVisibility.set(props.stepId, newMode)
  // Persist per step only where the view is editable.
  if (!props.canSaveViewport) return
  setVisibilityMutation.mutate({ command: { stepId: props.stepId, visibility: newMode } })
}

const savedNotification = ref(false)
const suppressLoading = ref(false)

function onSaveViewport(viewport: Viewport) {
  if (!props.stepId) return
  saveViewportMutation.mutate({ command: { stepId: props.stepId, ...viewport } })?.then(() => {
    savedNotification.value = true
    suppressLoading.value = true
    refetch()?.then(() => {
      suppressLoading.value = false
    })
  })
}

const savedHiddenArticleNumbers = contentStableComputed<string[]>(
  () => result.value?.product.bop.hiddenArticleNumbers ?? [],
)
const hiddenArticleNumbers = ref<string[]>([])
watch(savedHiddenArticleNumbers, (numbers) => (hiddenArticleNumbers.value = numbers), {
  immediate: true,
})

const setHiddenArticlesMutation = useMutation(gql`
  mutation BopStepViewerSetHiddenArticles($command: SetBopHiddenArticles!) {
    product {
      setBopHiddenArticles(command: $command)
    }
  }
`)

function onUpdateHiddenArticles(articleNumbers: string[]) {
  hiddenArticleNumbers.value = articleNumbers
  setHiddenArticlesMutation.mutate({ command: { bopId: props.bopId, articleNumbers } })
}

const saveArticleTransformsMutation = useMutation(gql`
  mutation SaveStepArticleTransforms($command: SaveStepArticleTransforms!) {
    product {
      saveStepArticleTransforms(command: $command) {
        articleId
      }
    }
  }
`)

function onSaveTransforms(transforms: StoredArticleTransform[]) {
  if (!props.stepId) return
  saveArticleTransformsMutation
    .mutate({ command: { stepId: props.stepId, transforms } })
    ?.then(() => {
      suppressLoading.value = true
      refetch()?.then(() => {
        suppressLoading.value = false
      })
    })
}

const targetedUpload = computed<BopUploadForViewer | null>(
  () => preferredStepUpload.value ?? lastUpload.value,
)

const isConverting = computed(() => {
  if (glbUrl.value) return false
  const status = targetedUpload.value?.glbStatus
  return status === GlbStatus.Queued || status === GlbStatus.Converting
})

const conversionFailed = computed(() => {
  if (isConverting.value || glbUrl.value) return false
  return targetedUpload.value?.glbStatus === GlbStatus.Failed
})

const hasGlbFile = computed(() => !!glbUrl.value || isConverting.value)
defineExpose({ refresh: () => refetch(), hasGlbFile })
</script>

<template>
  <bop-model-viewer
    :glb-url="glbUrl"
    :converting="isConverting"
    :conversion-failed="conversionFailed"
    :data-loading="loading && !suppressLoading && !glbUrl"
    :visible-mesh-hints="visibleMeshHints"
    :highlighted-mesh-hints="currentStepHighlightHints"
    :saved-viewport="savedViewport"
    :can-save-viewport="canSaveViewport"
    :show-warnings="canSaveViewport && !isConverting"
    :selectable="selectable"
    :selectable-hints="selectableHints"
    :ebom-tree-parts="ebomTreeParts"
    :hidden-article-numbers="hiddenArticleNumbers"
    :ebom-part-allocations="currentStepEbomPartAllocations"
    :known-article-names="knownArticleNames"
    :color-map="colorMap"
    :active-ebom-label="activeEbomLabel"
    :editable="canSaveViewport"
    :step-id="stepId"
    :transform-hints="moduleTransformHints"
    :editable-transform-hints="editableTransformHints"
    :article-transforms="currentStepArticleTransforms"
    :visibility-mode="visibilityMode"
    :in-step-group="currentStepInGroup"
    :show-controls-help="showControlsHelp"
    @update:visibility-mode="onVisibilityModeChange"
    @save-viewport="onSaveViewport"
    @save-transforms="onSaveTransforms"
    @unusable-part="handleUnusablePart"
    @allocate-part="handleAllocatePart"
    @save-instances="handleSaveInstances"
    @update:hidden-article-numbers="onUpdateHiddenArticles"
  >
    <template v-if="showModelPicker" #model-picker>
      <viewer-model-picker
        :options="modelOptions"
        :active-id="displayedUpload?.id ?? null"
        :automatic="!hasSelectedModel"
        :loading="setPreferredModelMutation.loading.value"
        :attach="overlayAttach"
        @select="onSelectModel"
      />
    </template>
  </bop-model-viewer>
  <notification-saved v-model="savedNotification" />
  <async-delete-dialog ref="deleteDialog" :attach="overlayAttach" />
  <v-snackbar
    v-model="feedbackOpen"
    :timeout="3000"
    :color="feedback.color"
    :attach="overlayAttach"
  >
    {{ feedback.message }}
  </v-snackbar>
</template>
